import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
    Lock,
    LogOut,
    RefreshCw,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Inbox,
    Loader2,
    MessageCircle,
    Newspaper,
    Mail,
    Flame,
    Image as ImageIcon,
    LayoutTemplate,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import AdminBlogPanel from "@/pages/AdminBlogPanel";
import AdminChatPanel from "@/pages/AdminChatPanel";
import AdminHotLeadsPanel from "@/pages/AdminHotLeadsPanel";
import AdminMediaPanel from "@/pages/AdminMediaPanel";
import AdminPageContentPanel from "@/pages/AdminPageContentPanel";
import { ADMIN_TOKEN_STORAGE_KEY, QUOTE_API_PATH } from "@/lib/quote-options";
import { PAGE_META } from "@/lib/seo-constants";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}${QUOTE_API_PATH}`;

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-CA", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const formatPhone = (digits) => {
    if (!digits) return "—";
    const d = String(digits).replace(/\D/g, "");
    if (d.length === 10) {
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return digits;
};

const Label = ({ children }) => (
    <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
        {children}
    </span>
);

const TokenGate = ({ onSubmit, error, loading }) => {
    const [tokenInput, setTokenInput] = useState("");

    const handle = (e) => {
        e.preventDefault();
        if (tokenInput.trim()) onSubmit(tokenInput.trim());
    };

    return (
        <Section dark className="py-24 md:py-32 min-h-[80vh] flex items-center" data-testid="admin-gate">
            <Container size="narrow">
                <div className="bg-[#2f2f2f] border border-white/10 p-8 md:p-10 max-w-md mx-auto">
                    <span className="flex h-12 w-12 items-center justify-center bg-brand-coral text-white mb-6">
                        <Lock className="h-6 w-6" strokeWidth={2.25} />
                    </span>
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-2">
                        Admin Access
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-wide text-white leading-tight mb-3">
                        Enter admin token
                    </h1>
                    <p className="text-sm text-white/65 mb-6">
                        This page is restricted. Provide your X-Admin-Token to view
                        quote submissions.
                    </p>
                    <form onSubmit={handle}>
                        <input
                            type="password"
                            value={tokenInput}
                            onChange={(e) => setTokenInput(e.target.value)}
                            placeholder="Admin token"
                            autoFocus
                            className="block w-full bg-brand-dark border-2 border-white/15 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-brand-cyan focus:outline-none transition-colors"
                            data-testid="admin-token-input"
                        />
                        {error && (
                            <p
                                className="mt-2 flex items-center gap-1.5 text-xs text-brand-coral"
                                data-testid="admin-gate-error"
                            >
                                <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                                {error}
                            </p>
                        )}
                        <BrandButton
                            type="submit"
                            variant="primary"
                            size="md"
                            className="w-full mt-5"
                            disabled={loading}
                            data-testid="admin-gate-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                                    Verifying…
                                </>
                            ) : (
                                "Unlock Admin"
                            )}
                        </BrandButton>
                    </form>
                </div>
            </Container>
        </Section>
    );
};

const StatusPill = ({ status }) => (
    <span className="inline-block bg-brand-cyan/15 text-brand-cyan text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5">
        {status || "new"}
    </span>
);

const SubmissionRow = ({ row, expanded, onToggle }) => (
    <>
        <tr
            onClick={onToggle}
            className={cn(
                "border-b border-brand-dark/10 cursor-pointer hover:bg-brand-offwhite transition-colors",
                expanded && "bg-brand-offwhite",
            )}
            data-testid={`admin-row-${row.id}`}
        >
            <td className="px-3 py-3 text-xs text-brand-dark/70 whitespace-nowrap">
                {formatDate(row.created_at)}
            </td>
            <td className="px-3 py-3 text-sm font-semibold text-brand-dark whitespace-nowrap">
                {row.full_name}
            </td>
            <td className="px-3 py-3 text-sm text-brand-dark whitespace-nowrap">
                <a
                    href={`tel:${row.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-brand-cyan"
                >
                    {formatPhone(row.phone)}
                </a>
            </td>
            <td className="px-3 py-3 text-sm text-brand-dark">
                <a
                    href={`mailto:${row.email}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-brand-cyan"
                >
                    {row.email}
                </a>
            </td>
            <td className="px-3 py-3 text-sm text-brand-dark whitespace-nowrap">
                {row.city}
                {row.postal_code ? ` · ${row.postal_code}` : ""}
            </td>
            <td className="px-3 py-3 text-xs text-brand-dark/80">
                {(row.services || []).join(", ")}
            </td>
            <td className="px-3 py-3 text-xs text-brand-dark/70 capitalize">
                {row.property_type}
            </td>
            <td className="px-3 py-3 text-xs text-brand-dark/70 capitalize">
                {row.project_size}
            </td>
            <td className="px-3 py-3 text-xs text-brand-dark/70 whitespace-nowrap">
                {row.timeline}
            </td>
            <td className="px-3 py-3 text-xs text-brand-dark/50 whitespace-nowrap">
                {row.source_page || "—"}
            </td>
            <td className="px-3 py-3">
                <StatusPill status={row.status} />
            </td>
            <td className="px-3 py-3 text-brand-gray">
                {expanded ? (
                    <ChevronUp className="h-4 w-4" strokeWidth={2.5} />
                ) : (
                    <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
                )}
            </td>
        </tr>
        {expanded && (
            <tr
                className="bg-brand-offwhite border-b border-brand-dark/10"
                data-testid={`admin-row-detail-${row.id}`}
            >
                <td colSpan={12} className="px-5 py-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <div>
                            <Label>Description</Label>
                            <p className="text-sm text-brand-dark whitespace-pre-wrap">
                                {row.description || "—"}
                            </p>
                        </div>
                        <div>
                            <Label>Best time to reach</Label>
                            <p className="text-sm text-brand-dark capitalize">
                                {row.best_time || "—"}
                            </p>
                            <Label>How did they hear about us</Label>
                            <p className="text-sm text-brand-dark">
                                {row.referral_source || "—"}
                            </p>
                        </div>
                        <div>
                            <Label>Submission ID</Label>
                            <p className="text-xs font-mono text-brand-dark/70 break-all">
                                {row.id}
                            </p>
                        </div>
                    </div>
                </td>
            </tr>
        )}
    </>
);

const Admin = () => {
    const [token, setToken] = useState(() =>
        typeof window !== "undefined"
            ? localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || ""
            : "",
    );
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [gateError, setGateError] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = (() => {
        const t = searchParams.get("tab") || "hot-leads";
        if (t === "submissions") return "submissions";
        if (t === "blog") return "blog";
        if (t === "chat") return "chat";
        if (t === "media") return "media";
        if (t === "page-content") return "page-content";
        return "hot-leads";
    })();
    const setActiveTab = (tab) => {
        const next = new URLSearchParams(searchParams);
        if (tab === "hot-leads") next.delete("tab");
        else next.set("tab", tab);
        setSearchParams(next, { replace: true });
    };

    // Polling for unacknowledged hot leads count — keeps the red badge
    // up-to-date on the Hot Leads tab regardless of which tab is active.
    const [unackHotLeads, setUnackHotLeads] = useState(0);
    const fetchUnackCount = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/hot-leads/unacknowledged-count`,
                { headers: { "X-Admin-Token": token } },
            );
            setUnackHotLeads(Number(res?.data?.count || 0));
        } catch {
            // silent — non-critical polling
        }
    }, [token]);
    useEffect(() => {
        if (!token) return undefined;
        fetchUnackCount();
        const id = setInterval(fetchUnackCount, 30_000);
        return () => clearInterval(id);
    }, [token, fetchUnackCount]);

    const fetchRows = useCallback(
        async (t = token) => {
            if (!t) return;
            setLoading(true);
            setError("");
            try {
                const res = await axios.get(API_URL, {
                    headers: { "X-Admin-Token": t },
                });
                setRows(Array.isArray(res.data) ? res.data : []);
                setGateError("");
            } catch (err) {
                const code = err?.response?.status;
                if (code === 401 || code === 403) {
                    setGateError("Invalid token. Please try again.");
                    setToken("");
                    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
                } else {
                    setError(
                        err?.response?.data?.detail ||
                            "Could not load submissions. Try refreshing.",
                    );
                }
            } finally {
                setLoading(false);
            }
        },
        [token],
    );

    useEffect(() => {
        if (token) fetchRows(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTokenSubmit = async (t) => {
        setLoading(true);
        setGateError("");
        try {
            const res = await axios.get(API_URL, {
                headers: { "X-Admin-Token": t },
            });
            setRows(Array.isArray(res.data) ? res.data : []);
            setToken(t);
            localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, t);
        } catch (err) {
            const code = err?.response?.status;
            if (code === 401 || code === 403) {
                setGateError("Invalid token. Please try again.");
            } else {
                setGateError("Network error. Please retry.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        setToken("");
        setRows([]);
        setExpandedId(null);
    };

    const total = useMemo(() => rows.length, [rows]);

    if (!token) {
        return (
            <>
                <Seo {...PAGE_META.admin} path="/admin" noindex />
                <TokenGate
                    onSubmit={handleTokenSubmit}
                    error={gateError}
                    loading={loading}
                />
            </>
        );
    }

    return (
        <Section className="py-12 md:py-16 min-h-[80vh]" data-testid="admin-page">
            <Seo {...PAGE_META.admin} path="/admin" noindex />
            <Container size="wide">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-2">
                            Reformity Admin
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none"
                            data-testid="admin-title"
                        >
                            Dashboard
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <BrandButton
                            variant="primary"
                            size="md"
                            onClick={handleLogout}
                            data-testid="admin-logout"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={2.25} />
                            Logout
                        </BrandButton>
                    </div>
                </div>

                {/* Tabs */}
                <div
                    className="flex items-center gap-1 mb-8 border-b border-brand-dark/15 overflow-x-auto"
                    role="tablist"
                    aria-label="Admin sections"
                    data-testid="admin-tabs"
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "hot-leads"}
                        onClick={() => setActiveTab("hot-leads")}
                        className={cn(
                            "relative inline-flex items-center gap-2 px-5 py-3 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap",
                            activeTab === "hot-leads"
                                ? "border-brand-coral text-brand-dark"
                                : "border-transparent text-brand-gray hover:text-brand-dark",
                        )}
                        data-testid="admin-tab-hot-leads"
                    >
                        <Flame className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                        Hot Leads
                        {unackHotLeads > 0 && (
                            <span
                                className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-brand-coral text-white text-[10px] font-bold rounded-full"
                                data-testid="admin-tab-hot-leads-badge"
                            >
                                {unackHotLeads}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "submissions"}
                        onClick={() => setActiveTab("submissions")}
                        className={cn(
                            "inline-flex items-center gap-2 px-5 py-3 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap",
                            activeTab === "submissions"
                                ? "border-brand-cyan text-brand-dark"
                                : "border-transparent text-brand-gray hover:text-brand-dark",
                        )}
                        data-testid="admin-tab-submissions"
                    >
                        <Mail className="h-4 w-4" strokeWidth={2.25} />
                        Quote Submissions
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "chat"}
                        onClick={() => setActiveTab("chat")}
                        className={cn(
                            "inline-flex items-center gap-2 px-5 py-3 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap",
                            activeTab === "chat"
                                ? "border-brand-cyan text-brand-dark"
                                : "border-transparent text-brand-gray hover:text-brand-dark",
                        )}
                        data-testid="admin-tab-chat"
                    >
                        <MessageCircle className="h-4 w-4" strokeWidth={2.25} />
                        Chat Sessions
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "blog"}
                        onClick={() => setActiveTab("blog")}
                        className={cn(
                            "inline-flex items-center gap-2 px-5 py-3 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap",
                            activeTab === "blog"
                                ? "border-brand-cyan text-brand-dark"
                                : "border-transparent text-brand-gray hover:text-brand-dark",
                        )}
                        data-testid="admin-tab-blog"
                    >
                        <Newspaper className="h-4 w-4" strokeWidth={2.25} />
                        Blog Posts
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "media"}
                        onClick={() => setActiveTab("media")}
                        className={cn(
                            "inline-flex items-center gap-2 px-5 py-3 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap",
                            activeTab === "media"
                                ? "border-brand-cyan text-brand-dark"
                                : "border-transparent text-brand-gray hover:text-brand-dark",
                        )}
                        data-testid="admin-tab-media"
                    >
                        <ImageIcon className="h-4 w-4" strokeWidth={2.25} />
                        Media Library
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "page-content"}
                        onClick={() => setActiveTab("page-content")}
                        className={cn(
                            "inline-flex items-center gap-2 px-5 py-3 text-xs sm:text-sm uppercase tracking-wider font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap",
                            activeTab === "page-content"
                                ? "border-brand-cyan text-brand-dark"
                                : "border-transparent text-brand-gray hover:text-brand-dark",
                        )}
                        data-testid="admin-tab-page-content"
                    >
                        <LayoutTemplate className="h-4 w-4" strokeWidth={2.25} />
                        Page Content
                    </button>
                </div>

                {activeTab === "submissions" ? (
                    <div data-testid="admin-tab-panel-submissions">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                            <div>
                                <h2 className="font-display text-3xl tracking-wide text-brand-dark leading-none">
                                    Quote Submissions
                                </h2>
                                <p className="mt-2 text-sm text-brand-dark/70" data-testid="admin-total">
                                    <span className="font-semibold text-brand-dark">{total}</span>{" "}
                                    submission{total === 1 ? "" : "s"} total — newest first.
                                </p>
                            </div>
                            <BrandButton
                                variant="outline"
                                tone="onLight"
                                size="md"
                                onClick={() => fetchRows()}
                                disabled={loading}
                                data-testid="admin-refresh"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                                ) : (
                                    <RefreshCw className="h-4 w-4" strokeWidth={2.25} />
                                )}
                                Refresh
                            </BrandButton>
                        </div>

                        {error && (
                            <div
                                className="mb-6 border-l-4 border-brand-coral bg-brand-coral/10 p-4 text-sm text-brand-dark"
                                data-testid="admin-error"
                            >
                                <p className="font-semibold flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                                    {error}
                                </p>
                            </div>
                        )}

                        {!loading && rows.length === 0 ? (
                            <div
                                className="bg-white border-2 border-dashed border-brand-dark/15 p-16 text-center"
                                data-testid="admin-empty"
                            >
                                <Inbox className="h-10 w-10 mx-auto text-brand-gray mb-4" strokeWidth={1.5} />
                                <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-2">
                                    No submissions yet.
                                </h3>
                                <p className="text-sm text-brand-dark/60">
                                    New quote requests will appear here as soon as homeowners
                                    submit the form.
                                </p>
                            </div>
                        ) : (
                            <div
                                className="bg-white border border-brand-dark/10 overflow-x-auto"
                                data-testid="admin-table-wrapper"
                            >
                                <table className="w-full text-left">
                                    <thead className="bg-brand-dark text-white text-[10px] uppercase tracking-wider font-semibold">
                                        <tr>
                                            <th className="px-3 py-3">Created</th>
                                            <th className="px-3 py-3">Name</th>
                                            <th className="px-3 py-3">Phone</th>
                                            <th className="px-3 py-3">Email</th>
                                            <th className="px-3 py-3">City</th>
                                            <th className="px-3 py-3">Services</th>
                                            <th className="px-3 py-3">Property</th>
                                            <th className="px-3 py-3">Size</th>
                                            <th className="px-3 py-3">Timeline</th>
                                            <th className="px-3 py-3">Source</th>
                                            <th className="px-3 py-3">Status</th>
                                            <th className="px-3 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <SubmissionRow
                                                key={row.id}
                                                row={row}
                                                expanded={expandedId === row.id}
                                                onToggle={() =>
                                                    setExpandedId(expandedId === row.id ? null : row.id)
                                                }
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : activeTab === "blog" ? (
                    <AdminBlogPanel token={token} />
                ) : activeTab === "media" ? (
                    <AdminMediaPanel token={token} />
                ) : activeTab === "page-content" ? (
                    <AdminPageContentPanel token={token} />
                ) : activeTab === "chat" ? (
                    <AdminChatPanel token={token} />
                ) : (
                    <AdminHotLeadsPanel
                        token={token}
                        onOpenChat={() => {
                            setActiveTab("chat");
                            // refresh count after navigating away
                            fetchUnackCount();
                        }}
                        onAcknowledged={() =>
                            setUnackHotLeads((n) => Math.max(0, n - 1))
                        }
                    />
                )}
            </Container>
        </Section>
    );
};

export default Admin;
